<template>
  <v-container fluid class="mt-16 px-6">
    <div>
      <v-card flat>
        <v-card-title class="d-flex align-center">
          <h2>Printable Forms Access</h2>
          <v-spacer></v-spacer>
          <v-btn color="success" text @click="openFormDialog('create')">
            <v-icon class="mr-2">mdi-plus</v-icon>Add Form
          </v-btn>
          <v-btn color="primary" text @click="refresh" :loading="loading">
            <v-icon class="mr-2">mdi-refresh</v-icon>Refresh
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="caption grey--text mb-4">
            Check a box to grant a form to a hat. Holders of that hat will see
            the form on their dashboard's Printable Forms card and can print or
            open it. Nothing is applied until you press Save Matrix.
          </div>

          <!-- Access matrix: rows = forms, columns = hats -->
          <v-simple-table class="elevation-1 matrix-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left form-col">Form</th>
                  <th
                    v-for="hat in hats"
                    :key="hat.id"
                    class="text-center hat-col"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="hat-label">
                          {{ abbreviate(hat.name) }}
                        </span>
                      </template>
                      <span>{{ hat.name }}</span>
                    </v-tooltip>
                  </th>
                  <th class="text-center actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td :colspan="hats.length + 2" class="text-center pa-4">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                  </td>
                </tr>
                <tr v-else-if="!forms.length">
                  <td
                    :colspan="hats.length + 2"
                    class="text-center pa-4 grey--text"
                  >
                    No printable forms found.
                  </td>
                </tr>
                <tr v-for="form in forms" :key="form.id">
                  <td class="form-col">
                    <span class="font-weight-medium"
                      >Form {{ form.form_number }}</span
                    >
                    &mdash; {{ form.title }}
                    <div class="caption grey--text">
                      <span v-if="form.om_part"
                        >OM Part {{ form.om_part }}</span
                      >
                      <span v-if="form.om_part && form.app_route">
                        &middot;
                      </span>
                      <span v-if="form.app_route"
                        >In-app: {{ form.app_route }}</span
                      >
                      <v-chip
                        v-if="form.file_path"
                        x-small
                        class="ml-1"
                        color="green"
                        text-color="white"
                      >
                        template
                      </v-chip>
                      <v-chip
                        v-if="!form.is_active"
                        x-small
                        class="ml-1"
                        color="grey"
                        text-color="white"
                      >
                        inactive
                      </v-chip>
                    </div>
                  </td>
                  <td
                    v-for="hat in hats"
                    :key="hat.id"
                    class="text-center hat-col"
                  >
                    <v-checkbox
                      :input-value="
                        assignments[form.id] &&
                        assignments[form.id].includes(hat.id)
                      "
                      @change="toggleAssignment(form.id, hat.id, $event)"
                      color="primary"
                      hide-details
                      dense
                      class="mt-0 pt-0 d-inline-flex shrink"
                    ></v-checkbox>
                  </td>
                  <td class="text-center actions-col">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="openFormDialog('edit', form)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Save -->
          <div class="mt-4 d-flex justify-end" v-if="isModified">
            <v-chip color="orange" text-color="white" class="mr-3">
              Unsaved changes
            </v-chip>
            <v-btn color="primary" @click="saveMatrix" :loading="saving">
              <v-icon class="mr-2">mdi-content-save</v-icon>
              Save Matrix
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Form create/edit dialog -->
      <v-dialog v-model="formDialog.show" persistent width="520">
        <v-card class="pa-5">
          <v-card-title>
            <h3>
              {{ formDialog.mode === "create" ? "Add Form" : "Edit Form" }}
            </h3>
          </v-card-title>
          <v-card-text>
            <v-form ref="formForm">
              <v-text-field
                label="Form Number"
                v-model.number="formDialog.form.form_number"
                type="number"
                min="1"
                :rules="[(v) => !!v || 'Form number is required']"
                prepend-icon="mdi-numeric"
              ></v-text-field>
              <v-text-field
                label="Title"
                v-model="formDialog.form.title"
                :rules="[(v) => !!v || 'Title is required']"
                prepend-icon="mdi-text"
              ></v-text-field>
              <v-text-field
                label="OM Part (e.g. 6.4)"
                v-model="formDialog.form.om_part"
                prepend-icon="mdi-book-open-page-variant"
                clearable
              ></v-text-field>
              <v-text-field
                label="App Route (leave blank for file-based forms)"
                v-model="formDialog.form.app_route"
                prepend-icon="mdi-link"
                hint="e.g. / — renders an Open button instead of Print"
                persistent-hint
                clearable
              ></v-text-field>
              <v-switch
                label="Active"
                v-model="formDialog.form.is_active"
                class="mt-2"
              ></v-switch>
              <v-file-input
                label="Template file (doc/docx/xls/xlsx/pdf, max 10 MB)"
                v-model="formDialog.template"
                prepend-icon="mdi-file-upload"
                show-size
                clearable
                :hint="currentFileHint"
                persistent-hint
              ></v-file-input>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="primary"
              @click="saveForm"
              :loading="formDialog.saving"
            >
              {{ formDialog.mode === "create" ? "Create" : "Save" }}
            </v-btn>
            <v-btn @click="formDialog.show = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      // Local editable copy of the matrix: { <form_id>: [hat_id, ...] }
      assignments: {},
      // Server snapshot for the dirty check.
      originalAssignments: {},
      formDialog: {
        show: false,
        mode: "create",
        saving: false,
        form: {},
        template: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      forms: "printable_forms/forms",
      hats: "permission/hats",
    }),
    isModified() {
      const normalize = (src) =>
        Object.keys(src)
          .sort()
          .map(
            (k) =>
              k +
              ":" +
              src[k]
                .slice()
                .sort((a, b) => a - b)
                .join(",")
          )
          .join("|");
      return (
        normalize(this.assignments) !== normalize(this.originalAssignments)
      );
    },
    currentFileHint() {
      const filePath = this.formDialog.form.file_path;
      return filePath
        ? "Current: " + filePath.split("/").pop()
        : "No template uploaded yet";
    },
  },
  methods: {
    ...mapActions({
      fetch_forms: "printable_forms/fetch_forms",
      save_form: "printable_forms/save_form",
      save_access: "printable_forms/save_access",
      fetch_hats: "permission/fetch_hats",
    }),

    // Initials of each word keeps ~21 hat columns compact; the full
    // name is shown in the header tooltip.
    abbreviate(name) {
      return name
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase())
        .join("");
    },

    toggleAssignment(formId, hatId, checked) {
      const current = this.assignments[formId] || [];
      let next;
      if (checked) {
        next = current.includes(hatId) ? current : [...current, hatId];
      } else {
        next = current.filter((id) => id !== hatId);
      }
      this.$set(this.assignments, formId, next);
    },

    async saveMatrix() {
      this.saving = true;
      try {
        await this.save_access(this.assignments);
        this.originalAssignments = JSON.parse(JSON.stringify(this.assignments));
        this.$alert({
          title: "Success",
          message: "Printable forms access matrix saved",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (err) {
        this.$alert({
          title: "Error",
          message: err.response?.data?.message || "Failed to save matrix",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.saving = false;
    },

    openFormDialog(mode, form = null) {
      this.formDialog.mode = mode;
      this.formDialog.template = null;
      this.formDialog.form =
        mode === "create"
          ? {
              form_number: null,
              title: "",
              om_part: "",
              app_route: "",
              is_active: true,
            }
          : {
              id: form.id,
              form_number: form.form_number,
              title: form.title,
              om_part: form.om_part,
              app_route: form.app_route,
              is_active: !!form.is_active,
              file_path: form.file_path,
            };
      this.formDialog.show = true;
    },

    async saveForm() {
      if (!this.$refs.formForm.validate()) return;
      this.formDialog.saving = true;
      try {
        const f = this.formDialog.form;
        const data = new FormData();
        if (f.id) {
          // The server validates `id` as a required body field, not just
          // the URL segment, so include it on updates.
          data.append("id", f.id);
        }
        data.append("form_number", f.form_number);
        data.append("title", f.title);
        data.append("om_part", f.om_part || "");
        data.append("app_route", f.app_route || "");
        data.append("is_active", f.is_active ? 1 : 0);
        if (this.formDialog.template) {
          data.append("template", this.formDialog.template);
        }
        await this.save_form({ id: f.id || null, data });
        this.formDialog.show = false;
        this.$alert({
          title: "Success",
          message: `Saved form "${f.title}"`,
          color: "success",
          icon: "mdi-check-circle",
        });
        await this.fetch_forms();
        this.syncAssignments(true);
      } catch (err) {
        this.$alert({
          title: "Error",
          message: err.response?.data?.message || "Failed to save form",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.formDialog.saving = false;
    },

    // Rebuild the local matrix from the server's hat_ids. Keeps unsaved
    // edits for forms the user has already toggled; only seeds entries
    // that don't exist locally yet (e.g. a newly added form).
    syncAssignments(preserveLocal = false) {
      const next = {};
      this.forms.forEach((form) => {
        if (preserveLocal && this.assignments[form.id] !== undefined) {
          next[form.id] = this.assignments[form.id];
        } else {
          next[form.id] = [...(form.hat_ids || [])];
        }
      });
      this.assignments = next;
      if (!preserveLocal) {
        this.originalAssignments = JSON.parse(JSON.stringify(next));
      }
    },

    async loadAll() {
      this.loading = true;
      await Promise.all([this.fetch_forms(), this.fetch_hats()]);
      this.syncAssignments();
      this.loading = false;
    },

    async refresh() {
      await this.loadAll();
    },
  },
  async created() {
    await this.loadAll();
  },
};
</script>

<style scoped>
.shrink {
  flex: 0 0 auto;
}
.form-col {
  min-width: 260px;
}
.hat-col {
  min-width: 44px;
  padding: 0 4px !important;
}
.hat-label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: default;
}
.actions-col {
  min-width: 64px;
}
</style>
